import React from 'react';
import { motion } from 'framer-motion';

interface HeroSectionProps {
  title?: {
    line1: string;
    line2: string;
    line3: string;
  };
  description?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonLink?: string;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  title = {
    line1: "We build",
    line2: "what should",
    line3: "exist."
  },
  description = "TND Labs is an independent venture studio founded by David and Tamara McKinney. We turn ideas into products, brands, technology, and businesses.",
  primaryButtonText = "Explore our ventures",
  secondaryButtonText = "Talk to us",
  primaryButtonLink = "#ventures",
  secondaryButtonLink = "#contact"
}) => {
  return (
    <section className="relative flex flex-col justify-center min-h-[92vh] px-6 md:px-12 pt-32 pb-24">
      <div className="max-w-7xl mx-auto w-full grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <motion.p
            className="text-xs uppercase tracking-eyebrow text-ink-muted mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Independent Venture Studio
          </motion.p>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-ink">
            <motion.span
              className="block"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              {title.line1}
            </motion.span>
            <motion.span
              className="block"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              {title.line2}
            </motion.span>
            <motion.span
              className="block italic"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              {title.line3}
            </motion.span>
          </h1>

          <motion.p
            className="text-lg md:text-xl text-ink-soft mt-10 max-w-lg leading-relaxed"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
          >
            {description}
          </motion.p>

          <motion.div
            className="mt-12 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.6 }}
          >
            <a
              href={primaryButtonLink}
              className="px-8 py-4 bg-ink text-white text-sm font-medium tracking-wide hover:bg-ink-soft transition-colors"
            >
              {primaryButtonText}
            </a>
            <a
              href={secondaryButtonLink}
              className="px-8 py-4 border border-ink-line text-ink text-sm font-medium tracking-wide hover:border-ink transition-colors"
            >
              {secondaryButtonText}
            </a>
          </motion.div>
        </div>

        {/* A constellation of bets — the only color in the studio's own identity */}
        <div className="md:col-span-5 hidden md:block">
          <div className="relative w-full aspect-square max-w-md ml-auto">
            <svg viewBox="0 0 400 400" className="w-full h-full" fill="none">
              <motion.line
                x1="130" y1="150" x2="250" y2="110"
                stroke="#e2ded6" strokeWidth="1"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.7, duration: 0.8 }}
              />
              <motion.line
                x1="130" y1="150" x2="220" y2="262"
                stroke="#e2ded6" strokeWidth="1"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
              />
              <motion.line
                x1="250" y1="110" x2="312" y2="200"
                stroke="#e2ded6" strokeWidth="1"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.9, duration: 0.8 }}
              />

              <motion.circle
                cx="130" cy="150" r="42" fill="#b8646f"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, y: [0, -7, 0] }}
                transition={{
                  scale: { delay: 0.75, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                  opacity: { delay: 0.75, duration: 0.5 },
                  y: { delay: 1.4, duration: 5, repeat: Infinity, ease: "easeInOut" },
                }}
              />
              <motion.circle
                cx="220" cy="262" r="31" fill="#b1803c"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, y: [0, 7, 0] }}
                transition={{
                  scale: { delay: 0.85, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                  opacity: { delay: 0.85, duration: 0.5 },
                  y: { delay: 1.5, duration: 4.5, repeat: Infinity, ease: "easeInOut" },
                }}
              />
              <motion.circle
                cx="312" cy="200" r="23" fill="#4d6d99"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, y: [0, -5, 0] }}
                transition={{
                  scale: { delay: 0.95, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                  opacity: { delay: 0.95, duration: 0.5 },
                  y: { delay: 1.6, duration: 4, repeat: Infinity, ease: "easeInOut" },
                }}
              />
              <motion.circle
                cx="250" cy="110" r="14" fill="none" stroke="#c9c4ba" strokeWidth="1.5" strokeDasharray="3 4"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1.05, duration: 0.5 }}
              />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};
